'use client';
import { ArrowRight } from 'lucide-react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { InputSearch, type InputSearchTone } from '@//components/InputSearch';
import { useMarketMetadata } from '@//providers/MarketSettingsProvider';

interface FaqSearchBarProps {
  initialQuery?: string;
  tone?: InputSearchTone;
}

export const FaqSearchBar = ({ initialQuery, tone = 'default' }: FaqSearchBarProps) => {
  const t = useTranslations('pages.faq.searchBar');
  const searchParams = useSearchParams();
  const [query, setQuery] = useState<string>(initialQuery || '');
  const router = useRouter();
  const marketMetadata = useMarketMetadata();

  useEffect(() => {
    const queryParam = searchParams.get('q');
    if (queryParam && queryParam !== query) {
      setQuery(queryParam);
    }
  }, [searchParams]);

  const handleSearch = () => {
    router.push(`${marketMetadata.pageLinks.faqSearch}?q=${encodeURIComponent(query)}`);
  };

  return (
    <InputSearch
      tone={tone}
      id="faq-search-input"
      label={t('label')}
      placeholder={t('placeholder')}
      aria-controls="faq-search-results"
      value={query}
      onChange={(e) => setQuery(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          handleSearch();
        }
      }}
      button={{
        label: (
          <>
            <ArrowRight aria-hidden="true" />
            <span className="sr-only">{t('title')}</span>
          </>
        ),
        title: t('title'),
        onClick: handleSearch,
      }}
      testId="faq-search-input"
    />
  );
};
